import {
  MarkLearnerCommentThreadsAsReadInput,
  MarkLearnerCommentThreadsAsReadMutation,
  MarkLearnerCommentThreadsAsReadMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useMutation } from "@outschool/ui-apollo";
import { useEffect } from "react";

import { MARK_LEARNER_COMMENT_THREADS_AS_READ_MUTATION } from "../../mutations/MarkLearnerCommentThreadsAsRead";
import { FeedbackThreadItem, PublicThreadItem } from "./types";

type CommentThreadKind = "feedback" | "classPost" | "assignment";

function getCommentThreadTarget(item: FeedbackThreadItem | PublicThreadItem): {
  kind: CommentThreadKind;
  uid: string;
} {
  if (item.kind === "feedback") {
    return { kind: "feedback", uid: item.assignmentClassPostUid };
  }
  return item.target.kind === "classPost"
    ? { kind: "classPost", uid: item.target.classPostUid }
    : { kind: "assignment", uid: item.target.assignmentClassPostUid };
}

function toMarkReadInput(
  sectionUid: string,
  kind: CommentThreadKind,
  uid: string,
): MarkLearnerCommentThreadsAsReadInput {
  switch (kind) {
    case "feedback":
      return { sectionUid, feedbackAssignmentClassPostUids: [uid] };
    case "classPost":
      return { sectionUid, classPostUids: [uid] };
    case "assignment":
      return { sectionUid, assignmentClassPostUids: [uid] };
  }
}

const PUBLIC_THREAD_TYPENAME = {
  classPost: "LearnerPublicCommentThread",
  assignment: "LearnerAssignmentPublicCommentThread",
} as const;

/**
 * Marks the open public or feedback thread read whenever it has unread
 * activity, including activity that arrives while the thread stays open.
 */
export function useMarkCommentThreadRead(
  sectionUid: string,
  item: FeedbackThreadItem | PublicThreadItem | null,
) {
  const [markRead] = useMutation<
    MarkLearnerCommentThreadsAsReadMutation,
    MarkLearnerCommentThreadsAsReadMutationVariables
  >(MARK_LEARNER_COMMENT_THREADS_AS_READ_MUTATION);

  const target = item ? getCommentThreadTarget(item) : null;
  const targetKind = target?.kind;
  const targetUid = target?.uid;
  const isUnread = (item?.unreadCount ?? 0) > 0;
  const latestMessageUid = item?.latestMessage?.uid;

  useEffect(() => {
    if (!targetKind || !targetUid || !isUnread) {
      return;
    }
    markRead({
      variables: { input: toMarkReadInput(sectionUid, targetKind, targetUid) },
      update(cache) {
        const sectionId = cache.identify({
          __typename: "Section",
          uid: sectionUid,
        });
        if (sectionId) {
          cache.modify({
            id: sectionId,
            fields: {
              learnerUnreadCommentThreadsCount(existing: number = 0) {
                return Math.max(0, existing - 1);
              },
            },
          });
        }
        if (targetKind === "feedback") {
          return;
        }
        const id = cache.identify({
          __typename: PUBLIC_THREAD_TYPENAME[targetKind],
          uid: targetUid,
        });
        if (id) {
          cache.modify({ id, fields: { isRead: () => true } });
        }
      },
    }).catch(() => {
      // The thread stays unread and is retried when new activity arrives.
    });
  }, [sectionUid, targetKind, targetUid, latestMessageUid, isUnread, markRead]);
}
